"use client"

import { useRef } from "react"
import { QRCodeCanvas } from "qrcode.react"
import { Download, MapPin, Calendar, Tag, DollarSign } from "lucide-react"
import config from "../config"

const EventDetailsTab = ({ event, loading }) => {
  const locationQrRef = useRef(null)
  const detailsQrRef = useRef(null)

  const downloadQRCode = (qrRef, fileName) => {
    if (qrRef.current) {
      const canvas = qrRef.current.querySelector("canvas")
      if (canvas) {
        const link = document.createElement("a")
        link.href = canvas.toDataURL("image/png")
        link.download = fileName
        link.click()
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!event) {
    return <div className="text-red-500">Event not found</div>;
  }

  return (
    <div className="space-y-8">
      {/* Event Description */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">About This Event</h2>
        {event.description && (
          <p className="text-gray-700 leading-relaxed text-lg">{event.description}</p>
        )}
      </div>

      {/* Event Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Location Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MapPin className="text-blue-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Location</h3>
          </div>
          {event.location && (
            <p className="text-gray-800 font-medium mb-3">{event.location}</p>
          )}
          {event.location_url && (
            <a
              href={event.location_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
            >
              View on Google Maps →
            </a>
          )}
        </div>

        {/* Date & Time Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Calendar className="text-green-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Date & Time</h3>
          </div>
          <p className="text-gray-800 font-medium">
            {new Date(event.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
          <p className="text-gray-600 mt-1">at {event.time}</p>
        </div>

        {/* Keywords Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Tag className="text-purple-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Keywords</h3>
          </div>
          {event.keywords ? (
            <div className="flex flex-wrap gap-2">
              {Array.isArray(event.keywords)
                ? event.keywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                  >
                    {keyword}
                  </span>
                ))
                : event.keywords.split(',').map((keyword, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                  >
                    {keyword.trim()}
                  </span>
                ))}
            </div>
          ) : (
            <p className="text-gray-600">No keywords added</p>
          )}
        </div>
      </div>

      {/* Payment Information Section */}
      {event.is_paid_event && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Information</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Ticket Price:</span>
              <span className="font-semibold">KES {parseFloat(event.ticket_price).toLocaleString()}</span>
            </div>
            {event.payment_instructions && (
              <div>
                <p className="text-gray-600 mb-1">Payment Instructions:</p>
                <p className="bg-gray-50 p-3 rounded-md text-gray-800 whitespace-pre-line">
                  {event.payment_instructions}
                </p>
              </div>
            )}
            {event.payment_deadline && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Payment Deadline:</span>
                <span className="font-medium">
                  {new Date(event.payment_deadline).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR Codes Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {event.location_url && (
          <div className="bg-white rounded-xl shadow-lg">
            <div className="p-8 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Location QR Code</h3>
              <p className="text-gray-600 mb-6">Scan to open location in maps</p>
            </div>
            <div className="flex flex-col items-center gap-6 p-8 bg-gray-50 rounded-lg">
              <div ref={locationQrRef} className="bg-white rounded-lg shadow-sm p-4">
                <QRCodeCanvas value={event.location_url} size={150} />
              </div>
              <button
                onClick={() => downloadQRCode(locationQrRef, "location_qr_code.png")}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg active:scale-95"
              >
                <Download size={18} />
                Download QR Code
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg">
          <div className="p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Event Details QR Code</h3>
            <p className="text-gray-600 mb-6">Scan for complete event information</p>
          </div>
          <div className="flex flex-col items-center gap-6 p-8 bg-gray-50 rounded-lg">
            <div ref={detailsQrRef} className="bg-white rounded-lg shadow-sm p-4">
              <QRCodeCanvas value={`${window.location.origin}/events/${event.slug}`} size={150} />
            </div>
            <button
              onClick={() => downloadQRCode(detailsQrRef, "event_details_qr_code.png")}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg active:scale-95"
            >
              <Download size={18} />
              Download QR Code
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EventDetailsTab
